import BenchlingConfig from 'BenchlingConfig';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import type {StoichiometryCustomEvalOptions} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  GetIupacNameFromSmilesQuery as GetIupacNameFromSmilesQueryResponse,
  GetIupacNameFromSmilesQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetIupacNameFromSmilesQuery';
import {
  GetIupacNameQuery as GetIupacNameQueryResponse,
  GetIupacNameQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetIupacNameQuery';
import {
  GET_IUPAC_NAME_FROM_SMILES_QUERY,
  GET_IUPAC_NAME_QUERY,
} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  CustomResolutionQueryType,
  GetIupacNameFromSmilesQuery,
  GetIupacNameQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

export async function bulkResolveGetIupacNameToServerResolutionResult(
  queries: Array<GetIupacNameQuery>
): Promise<Array<ServerResolutionResult>> {
  const moleculeIds = queries.map((query) => query.moleculeId);

  const queryResult = await doQueryWithoutCache<GetIupacNameQueryResponse, GetIupacNameQueryVariables>(
    {
      query: GET_IUPAC_NAME_QUERY,
    },
    {variables: {moleculeIds}}
  );

  // Build a map of moleculeId -> iupac name for efficient lookup
  const iupacNameByMoleculeId = new Map<string, string | null>();
  for (const molecule of queryResult.molecules) {
    if (isInaccessible(molecule)) {
      iupacNameByMoleculeId.set(molecule.invalidId, null);
    } else {
      const iupacName = molecule.currentVersion.chemicalStructure.iupacName;
      if (iupacName.__typename === 'StringPropertyNode') {
        iupacNameByMoleculeId.set(molecule.id, iupacName.value);
      } else {
        iupacNameByMoleculeId.set(molecule.id, null);
      }
    }
  }

  // Return results in the same order as the input queries
  return queries.map((query) => {
    const iupacName = iupacNameByMoleculeId.get(query.moleculeId);
    if (iupacName === undefined) {
      // Molecule was not returned by the server (e.g. deleted between query construction and execution)
      return makeCustomResolutionResultFromTypedValue(null, 'Could not retrieve IUPAC name');
    }
    if (iupacName == null) {
      // Molecule exists but has no IUPAC name in PubChem — leave cell empty, no error
      return makeCustomResolutionResultFromTypedValue(null);
    }
    return makeCustomResolutionResultFromTypedValue({
      type: FieldType.TEXT,
      value: iupacName,
    });
  });
}

export async function bulkResolveGetIupacNameFromSmilesToServerResolutionResult(
  queries: Array<GetIupacNameFromSmilesQuery>
): Promise<Array<ServerResolutionResult>> {
  // The backend field is keyed by a single SMILES string (there is no batch variant), so fire one
  // query per unique canonical SMILES. Apollo's batch HTTP link coalesces these into a single
  // request, and PubChemDataLoader deduplicates by canonical SMILES server-side.
  const uniqueSmiles = [...new Set(queries.map((query) => query.canonicalSmiles))];
  const iupacNameBySmiles = new Map<string, string | null>();
  await Promise.all(
    uniqueSmiles.map(async (smiles) => {
      const queryResult = await doQueryWithoutCache<
        GetIupacNameFromSmilesQueryResponse,
        GetIupacNameFromSmilesQueryVariables
      >({query: GET_IUPAC_NAME_FROM_SMILES_QUERY}, {variables: {smiles}});
      const {iupacNameFromSmiles} = queryResult;
      iupacNameBySmiles.set(
        smiles,
        iupacNameFromSmiles.__typename === 'StringPropertyNode' ? iupacNameFromSmiles.value : null
      );
    })
  );

  return queries.map((query) => {
    const iupacName = iupacNameBySmiles.get(query.canonicalSmiles);
    if (iupacName == null) {
      // No IUPAC name available (unknown compound, PubChem error, or flag off) — leave the cell
      // empty with no error so the UI degrades gracefully.
      return makeCustomResolutionResultFromTypedValue(null);
    }
    return makeCustomResolutionResultFromTypedValue({
      type: FieldType.TEXT,
      value: iupacName,
    });
  });
}

export function getIupacNameColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: StoichiometryCustomEvalOptions,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  // The molecule ID comes from the first cell dependency (the Name column containing the bioentity)
  const bioentityTypedValue = cellDependencies[0];
  // If there's no bioentity in the dependency, the row may be an unrecognized molecule: it carries
  // only a canonicalSmiles in its row metadata (no registered entity). In that case resolve the
  // IUPAC name directly from the SMILES via PubChem, gated behind the feature flag.
  if (bioentityTypedValue == null || bioentityTypedValue.type !== BaseTypedValueType.BIOENTITY) {
    const canonicalSmiles = customEvalOptions?.rowMetadata?.canonicalSmiles;
    if (
      BenchlingConfig.CHEMISTRY_ENABLE_PUBCHEM_IUPAC_GENERATION &&
      canonicalSmiles != null &&
      canonicalSmiles.length > 0
    ) {
      const {resolvedValue, error} = resolveFromResolutionQuery(
        typedValue,
        {
          customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_IUPAC_NAME_FROM_SMILES,
          canonicalSmiles,
          type: ResolutionQueryType.CUSTOM,
        },
        makeTypedValueFromCustomResolvedValue
      );
      return {val: resolvedValue, error, locked: false};
    }
    return {val: typedValue, error: null, locked: false};
  }
  const moleculeId = bioentityTypedValue.bioentitySummary.id;
  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_IUPAC_NAME,
      moleculeId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
